import type { RouteLocationNormalizedLoaded } from 'vue-router'

/**
 * Which top-level section of the console the current page belongs to: 'attendance', 'payroll',
 * 'system' (organization settings), and so on.
 *
 * Route nesting alone cannot answer this. Several pages live under one section in the router but
 * belong to another from the user's point of view -- device settings sit under Settings yet are
 * part of Devices -- so the rules match on route-name prefixes as well. The header breadcrumbs
 * use the result as their second level, and the sidebar's section hubs use it to decide which hub
 * is highlighted.
 */
export function getActiveSection (route: Pick<RouteLocationNormalizedLoaded, 'name' | 'path' | 'matched'>): string {
  if (route.matched.some(r => r.name === 'reports')) {
    return 'reports'
  }
  if (route.path.includes('/schedule/settings')) {
    return 'schedule'
  }
  if (route.matched.some(r => r.name === 'settings')) {
    return 'system'
  }
  const currentName = typeof route.name === 'string' ? route.name : ''
  if (
    currentName.startsWith('clock-log') ||
    currentName.startsWith('punch') ||
    currentName.startsWith('entry-state') ||
    currentName.startsWith('timecard') ||
    currentName.startsWith('shiftcard') ||
    currentName.startsWith('shift-extend') ||
    currentName.startsWith('attendance-settings') ||
    currentName === 'geomap' ||
    currentName.startsWith('discipline')
  ) {
    return 'attendance'
  }
  if (currentName.startsWith('schedule') || currentName === 'schedules' || currentName.startsWith('shift-list')) {
    return 'schedule'
  }
  if (currentName.startsWith('device') || currentName.startsWith('geo-rule') || currentName.startsWith('settings-device')) {
    return 'devices'
  }
  if (currentName.startsWith('employee') || currentName.startsWith('health-safety') || currentName.startsWith('briefing-type') || currentName.startsWith('users-settings') || currentName === 'users-index') {
    return 'users'
  }
  if (currentName.startsWith('timeoff') || currentName.startsWith('settings-timeoff')) {
    return 'timeoff'
  }
  if (currentName.startsWith('form')) {
    return 'forms'
  }
  if (currentName.startsWith('costing') || currentName.startsWith('settings-costing') || currentName.startsWith('job-')) {
    return 'costing'
  }
  if (currentName.startsWith('payroll') || currentName.startsWith('settings-payroll')) {
    return 'payroll'
  }
  if (currentName.startsWith('system-ops')) {
    return 'systemops'
  }
  if (currentName.startsWith('report') || currentName === 'reports-index' || currentName.startsWith('absent') || currentName.startsWith('tardy') || currentName === 'roll-call') {
    return 'reports'
  }
  if (
    currentName === 'settings' ||
    currentName.startsWith('settings-org') ||
    currentName.startsWith('org-settings') ||
    currentName.startsWith('org-') ||
    currentName === 'customfields' ||
    currentName === 'department-list' ||
    currentName === 'label-list' ||
    currentName === 'org-unit-list' ||
    currentName.startsWith('billing-') ||
    currentName === 'integrations' ||
    currentName.startsWith('discipline-policy')
  ) {
    return 'system'
  }
  if (currentName === 'dashboard') {
    return 'overview'
  }
  if (currentName === 'byxbi') {
    return 'byxbi'
  }
  return currentName || 'overview'
}
